/**
 * The row history panel's form state, kept apart from the component so the
 * rules about it can be tested without rendering anything.
 */
import type {
  RowHistoryAction,
  RowHistorySettings,
  RowHistorySettingsRequest,
  RowHistorySource,
} from '@/api/types'

export const HISTORY_ACTIONS: readonly RowHistoryAction[] = [
  'created',
  'updated',
  'deleted',
  'restored',
]

export const HISTORY_SOURCES: readonly RowHistorySource[] = [
  'web',
  'api_token',
  'mcp',
  'system',
]

export interface HistoryDraft {
  /** The window as typed; empty keeps the plan's. */
  retention: string
  disabledActions: RowHistoryAction[]
  disabledSources: RowHistorySource[]
}

export function draftFrom(settings: RowHistorySettings): HistoryDraft {
  return {
    retention: settings.retention_days === null ? '' : String(settings.retention_days),
    disabledActions: [...settings.disabled_actions],
    disabledSources: [...settings.disabled_sources],
  }
}

export type RetentionProblem = 'invalid' | 'exceedsPlan'

/**
 * Why the typed window cannot be saved, or null when it can.
 *
 * @param retention The window as typed.
 * @param planDays The plan's window; null when it never prunes.
 */
export function retentionProblem(
  retention: string,
  planDays: number | null,
): RetentionProblem | null {
  const text = retention.trim()
  if (text === '') return null
  if (!/^\d+$/.test(text) || Number(text) < 1) return 'invalid'
  if (planDays !== null && Number(text) > planDays) return 'exceedsPlan'
  return null
}

export function historyBody(draft: HistoryDraft): RowHistorySettingsRequest {
  const text = draft.retention.trim()
  return {
    retention_days: text === '' ? null : Number(text),
    disabled_actions: draft.disabledActions,
    disabled_sources: draft.disabledSources,
  }
}

/** `list` with `item` added when `on` is false, removed when it is true. */
export function toggled<T>(list: readonly T[], item: T, on: boolean): T[] {
  const without = list.filter((one) => one !== item)
  return on ? without : [...without, item]
}
